import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";

interface ShortcutRowProps {
  keys: string[];
  description: string;
}

function ShortcutRow({ keys, description }: ShortcutRowProps) {
  return (
    <div className="flex items-center justify-between py-1.5">
      <span className="text-sm text-muted-foreground">{description}</span>
      <div className="flex items-center gap-1">
        {keys.map((k, i) => (
          <span key={i} className="flex items-center gap-1">
            {i > 0 && (
              <span className="text-[10px] text-muted-foreground">+</span>
            )}
            <kbd className="inline-flex items-center justify-center min-w-[1.75rem] h-6 px-1.5 rounded border border-border bg-muted text-[11px] font-mono font-semibold text-foreground shadow-sm">
              {k}
            </kbd>
          </span>
        ))}
      </div>
    </div>
  );
}

interface KeyboardShortcutsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export default function KeyboardShortcutsDialog({
  open,
  onOpenChange,
}: KeyboardShortcutsDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="text-base font-semibold">
            Raccourcis clavier
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Global */}
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">
              Global
            </p>
            <div className="space-y-0.5">
              <ShortcutRow keys={["⌘ / Ctrl", "K"]} description="Ouvrir la palette de commandes" />
              <ShortcutRow keys={["N"]} description="Nouvelle capture" />
              <ShortcutRow keys={["?"]} description="Afficher les raccourcis" />
              <ShortcutRow keys={["Esc"]} description="Fermer / Annuler" />
            </div>
          </div>

          <Separator />

          {/* Navigation */}
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">
              Navigation
            </p>
            <div className="space-y-0.5">
              <ShortcutRow keys={["T"]} description="Aller à Aujourd'hui" />
              <ShortcutRow keys={["I"]} description="Aller à l'Inbox" />
              <ShortcutRow keys={["P"]} description="Aller aux Projets" />
            </div>
          </div>

          <Separator />

          {/* Capture */}
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">
              Capture
            </p>
            <div className="space-y-0.5">
              <ShortcutRow
                keys={["N"]}
                description="Ouvrir la fenêtre de capture"
              />
              <ShortcutRow
                keys={["⌘", "↵"]}
                description="Soumettre la capture"
              />
            </div>
          </div>

          <Separator />

          {/* Tip */}
          <p className="text-xs text-muted-foreground leading-relaxed">
            Les raccourcis sont désactivés lorsqu'un champ de texte est actif.
            Appuyez sur{" "}
            <kbd className="inline-flex items-center justify-center min-w-[1.75rem] h-5 px-1 rounded border border-border bg-muted text-[10px] font-mono font-semibold">
              Esc
            </kbd>{" "}
            pour quitter un champ avant d'utiliser un raccourci.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
